import { IconDots } from "@tabler/icons-react";
import { QueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLoaderData, useParams } from "react-router-dom";

import { STALE_TIME } from "#app/http/ReactQueryProvider";
import { Button } from "#components/atoms/Button/Button";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { Page } from "#components/templates/Page/Page";
import { useAllQuoteRequestLinesBulk } from "#modules/quoteRequestLines/view/useAllQuoteRequestLinesBulk";
import { quoteRequestQuery } from "#modules/quoteRequests/infra/quoteRequest.query";
import { useQuoteRequest } from "#modules/quoteRequests/view/useQuoteRequest";
import { QuoteRequestV2View } from "#modules/quoteRequestsV2/view/templates/QuoteRequestV2.view";
import { ROUTES } from "#shared/routes";

const loader = (queryClient: QueryClient, quoteRequestId: string | undefined) => async () => {
  const quoteRequest = await queryClient.fetchQuery({
    ...quoteRequestQuery(quoteRequestId),
    staleTime: STALE_TIME,
  });
  return { quoteRequest };
};

const QuoteRequestV2PageLayout = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();

  return (
    <Page>
      <Page.Header
        actionLeftSection={
          <IconButton variant="tertiary" size="sm" aria-label="more options" disabled>
            <IconDots size={16} />
          </IconButton>
        }
        actionRightSection={
          <>
            <Button variant="tertiary" size="sm" disabled>
              {t("quoteRequestV2.exportQuote")}
            </Button>
            <Button variant="primary" size="sm" disabled>
              {t("quoteRequestV2.createQuote")}
            </Button>
          </>
        }
      />
      <Page.Content>{children}</Page.Content>
    </Page>
  );
};

export const QuoteRequestV2Page = () => {
  const { quoteRequestId } = useParams();

  const { quoteRequest: initialQuoteRequest } = useLoaderData() as Awaited<
    ReturnType<ReturnType<typeof loader>>
  >;

  const { quoteRequest, isLoading: isLoadingQuoteRequest } = useQuoteRequest({
    quoteRequestId,
    options: { initialData: initialQuoteRequest },
  });

  const [initialQuoteRequestLineIds, setInitialQuoteRequestLineIds] = useState<string[]>([]);

  const quoteRequestLineIds = useMemo(
    () => quoteRequest?.quoteRequestLines.map((line) => line.id) || [],
    [quoteRequest],
  );

  useEffect(() => {
    if (initialQuoteRequestLineIds.length === 0) {
      setInitialQuoteRequestLineIds(quoteRequestLineIds);
    }
  }, [initialQuoteRequestLineIds, quoteRequestLineIds]);

  const { quoteRequestLines, isInitialLoading: isLoadingQuoteRequestLines } =
    useAllQuoteRequestLinesBulk(initialQuoteRequestLineIds, quoteRequestLineIds);

  if (isLoadingQuoteRequest) {
    return (
      <QuoteRequestV2PageLayout>
        <QuoteRequestV2View
          quoteRequest={null}
          isLoading={true}
          quoteRequestLines={[]}
          isLinesInitialLoading={true}
        />
        ;
      </QuoteRequestV2PageLayout>
    );
  }

  if (!quoteRequest) {
    return <Navigate to={ROUTES.v2QuoteRequests} />;
  }

  return (
    <QuoteRequestV2PageLayout>
      <QuoteRequestV2View
        quoteRequest={quoteRequest}
        isLoading={false}
        quoteRequestLines={quoteRequestLines}
        isLinesInitialLoading={isLoadingQuoteRequestLines}
      />
    </QuoteRequestV2PageLayout>
  );
};
